import { Badge, Button, Collapse, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkedContactItem } from 'src/Components/EntityLinks/LinkedContactItem';
import { LinkedDocumentItem } from 'src/Components/EntityLinks/LinkedDocumentItem';
import { LinkedEntityActionMenu } from 'src/Components/EntityLinks/LinkedEntityActionMenu';
import { LinkedEventItem } from 'src/Components/EntityLinks/LinkedEventItem';
import { LinkedExpenseItem } from 'src/Components/EntityLinks/LinkedExpenseItem';
import { LinkedInventoryItem } from 'src/Components/EntityLinks/LinkedInventoryItem';
import { LinkedMemoryItem } from 'src/Components/EntityLinks/LinkedMemoryItem';
import { LinkedProjectItem } from 'src/Components/EntityLinks/LinkedProjectItem';
import { LinkedRoomItem } from 'src/Components/EntityLinks/LinkedRoomItem';
import { LinkedTaskItem } from 'src/Components/EntityLinks/LinkedTaskItem';
import {
  GROUP_PREVIEW_SIZE,
  MESSAGE_GROUP,
} from 'src/Components/EntityLinks/utils/entityLinkConstants';
import { getGroupIcon } from 'src/Components/EntityLinks/utils/entityLinkIcons';
import { LinkableEntityType, LinkedEntityDto } from 'src/Services/API/EntityLinks.api';

import styles from './LinkedEntityGroup.module.scss';
import { Props } from './LinkedEntityGroup.types';

const itemKey = (item: LinkedEntityDto): string =>
  `${item.linkOrigin}-${item.businessRelationKind ?? 'generic'}-${item.linkId ?? item.entityId}`;

const LinkedEntityGroup = ({
  groupKey,
  items,
  progress,
  onItemClick,
  onAddClick,
  sourceType,
  sourceId,
}: Props) => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(true);
  const [showAll, setShowAll] = useState(false);
  const GroupIcon = getGroupIcon(groupKey);
  const isDocumentSection = groupKey === LinkableEntityType.VirtualFile;
  // Ordinary messages are only reached from the wall, not linked from a fiche.
  const canAdd = groupKey !== MESSAGE_GROUP;
  const visibleItems = showAll ? items : items.slice(0, GROUP_PREVIEW_SIZE);

  const renderItem = (item: LinkedEntityDto) => {
    const onClick = () => onItemClick(item);
    switch (item.entityType) {
      case LinkableEntityType.Task:
        return <LinkedTaskItem item={item} onClick={onClick} />;
      case LinkableEntityType.Contact:
        return <LinkedContactItem item={item} onClick={onClick} />;
      case LinkableEntityType.VirtualFile:
        return <LinkedDocumentItem item={item} onClick={onClick} />;
      case LinkableEntityType.Expense:
        return <LinkedExpenseItem item={item} onClick={onClick} />;
      case LinkableEntityType.Project:
        return <LinkedProjectItem item={item} onClick={onClick} />;
      case LinkableEntityType.CommunityFeedItem:
        return <LinkedMemoryItem item={item} onClick={onClick} />;
      case LinkableEntityType.Room:
        return <LinkedRoomItem item={item} onClick={onClick} />;
      case LinkableEntityType.Event:
        return <LinkedEventItem item={item} onClick={onClick} />;
      case LinkableEntityType.InventoryItem:
        return <LinkedInventoryItem item={item} onClick={onClick} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <UnstyledButton onClick={toggle} className={styles.toggle}>
          <GroupIcon size={16} />
          <span className={styles.label}>
            {t(`entityLinks.groups.${groupKey}.plural`, groupKey)}
          </span>
          <Badge size="sm" variant="light" color="gray">
            {progress ? `${progress.done}/${progress.total}` : items.length}
          </Badge>
          {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        </UnstyledButton>
        {canAdd && (
          <Tooltip
            label={t(
              'entityLinks.documentSearchComingSoon',
              "La liaison de documents arrivera bientôt — pour l'instant, ajoutez vos documents depuis le module Documents."
            )}
            disabled={!isDocumentSection}
            withinPortal
            multiline
            w={260}
          >
            {/* A real `disabled` button swallows pointer events, so the Tooltip above
                would never show on hover — data-disabled + a no-op onClick keep hover
                working while still preventing the action. */}
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              data-disabled={isDocumentSection || undefined}
              onClick={isDocumentSection ? undefined : onAddClick}
            >
              {t('entityLinks.add', 'Ajouter {{type}}', {
                type: t(`entityLinks.groups.${groupKey}.singular`, groupKey),
              })}
            </Button>
          </Tooltip>
        )}
      </div>
      <Collapse in={opened}>
        {items.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t(
              `entityLinks.groups.${groupKey}.emptyState`,
              'Aucun élément rattaché pour l’instant'
            )}
          </Text>
        ) : (
          <div className={styles.items}>
            {visibleItems.map(item => (
              <div key={itemKey(item)} className={styles.itemRow}>
                <div className={styles.itemCard}>{renderItem(item)}</div>
                <LinkedEntityActionMenu item={item} sourceType={sourceType} sourceId={sourceId} />
              </div>
            ))}
            {items.length > GROUP_PREVIEW_SIZE && (
              <Button variant="subtle" size="xs" onClick={() => setShowAll(value => !value)}>
                {showAll
                  ? t('entityLinks.showLess', 'Voir moins')
                  : t('entityLinks.showAll', 'Voir les {{count}}', { count: items.length })}
              </Button>
            )}
          </div>
        )}
      </Collapse>
    </div>
  );
};

export default LinkedEntityGroup;
